import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Sparkles,
  Code2,
  Users,
  GraduationCap,
  ExternalLink,
  ChevronRight,
  Terminal,
  FileCode,
  CheckCircle2,
  Lightbulb,
  Copy,
  BookOpen,
  ArrowRight,
  Layers,
  HelpCircle,
  FolderOpen,
  Clock,
} from 'lucide-react'
import CopyBlock from '../components/CopyBlock'
import AdminNav from '../components/AdminNav'
import { useAuth } from '../context/AuthContext'
import { listDrafts } from '../lib/aiStudioService'

const PROMPT_PAGED_LESSON = `I want to build a new training module for Finder University in src/modules/.

Topic: [e.g. Regex in Google Sheets / BigQuery to Sheets Connected Sheets / Advanced INDEX MATCH]
Track: [e.g. sheets / apps-script / claude-basics / ai-finance]

Requirements:
1. Use the PagedLesson component pattern from src/components/PagedLesson.jsx and see ModuleSumifs.jsx or ModuleTroubleshooting.jsx as a reference.
2. Structure it into 3-4 progressive pages:
   - Page 1: Concept, why it matters at Finder, and simple syntax overview with visual diagram.
   - Page 2: Step-by-step worked example with real business data.
   - Page 3: Common edge cases, debugging gotchas, and pro tips.
   - Page 4: Interactive recap quiz with 2-3 questions.
3. Register the new module in src/data/modules.js with id, title, description, and phase.
4. Add a unit test in test/modules.test.js or test/moduleContent.test.js.
5. Run 'npm test' and 'npm run build' to verify everything passes.`

const PROMPT_SHEET_SUBMISSION = `I want to create a hands-on Google Sheet practice exercise where trainees make a copy of a template sheet and paste their URL to get auto-checked.

Topic: [e.g. Pivot Table Reporting Challenge / Multi-tab Financial Consolidation]
Template Sheet URL: [Google Sheet URL]

Requirements:
1. Create a new module in src/modules/ using SheetSubmission.jsx (see Module1.jsx or Module4.jsx as reference).
2. Define the automated verification logic in src/lib/sheetsCheck.js to inspect cell formulas, sheet structure, and calculated outputs.
3. Add corresponding check unit tests in test/sheetsCheck.test.js.
4. Register the module in src/data/modules.js and verify with 'npm test'.`

const PROMPT_CUSTOM_DEGREE = `I want to create a new custom Degree curriculum for my team at Finder.

Degree Name: [e.g. "Ad Operations Lead" or "Commercial Analyst"]
Target Audience: [e.g. "Commercial and Merchandising crew"]
Included Tracks/Courses: [e.g. "sheets", "claude-basics", "ai-finance"]

Please:
1. Update src/data/degrees.js to add this degree to the initial SEED_DEGREES list.
2. Ensure slugify and degree resolution works cleanly with existing tests.
3. Run 'npm test' to verify test/degrees.test.js passes.`

export default function BuildModulesPage() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('lesson')
  const [drafts, setDrafts] = useState([])
  const [loadingDrafts, setLoadingDrafts] = useState(true)

  useEffect(() => {
    listDrafts(user)
      .then((res) => setDrafts(res || []))
      .catch((e) => console.error('Failed to load drafts:', e))
      .finally(() => setLoadingDrafts(false))
  }, [user])

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      {user?.isAdmin && <AdminNav />}

      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-900 via-slate-900 to-purple-950 p-8 text-white shadow-xl sm:p-12">
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-indigo-500/20 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-indigo-300 border border-indigo-400/30">
            <Sparkles className="h-3.5 w-3.5" />
            Open Source & Collaborative
          </div>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl">
            Build Your Own Training Modules
          </h1>
          <p className="mt-3 text-base text-slate-300 sm:text-lg">
            Finder University empowers everyone to build training modules. You can use our interactive <strong className="text-white">AI Module Studio</strong> to generate lessons with natural language, or contribute directly via code.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/admin/studio"
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 px-5 py-3 text-sm font-bold text-white shadow-lg hover:from-indigo-600 hover:to-purple-700 transition-all hover:scale-102 active:scale-98"
            >
              <Sparkles className="h-4 w-4" />
              Launch AI Module Studio
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="https://github.com/finderau/sheets-training"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-4 py-3 text-sm font-semibold text-white backdrop-blur-xs hover:bg-white/20 transition-colors border border-white/10"
            >
              <Code2 className="h-4 w-4" />
              GitHub Repository
              <ExternalLink className="h-3.5 w-3.5 opacity-70" />
            </a>
            {(user?.isAdmin || user?.isManager) && (
              <Link
                to="/admin"
                className="inline-flex items-center gap-2 rounded-2xl bg-purple-600/30 px-5 py-3 text-sm font-semibold text-purple-200 border border-purple-500/40 hover:bg-purple-600/50 transition-colors"
              >
                <Users className="h-4 w-4" />
                Admin Dashboard
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* In-Progress Drafts Section */}
      {drafts.length > 0 && (
        <div className="mt-10 rounded-3xl border border-indigo-100 bg-gradient-to-b from-indigo-50/60 to-white p-6 sm:p-8 shadow-xs">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-indigo-100 text-indigo-700">
                <FolderOpen className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Your In-Progress Module Drafts</h2>
                <p className="text-xs text-slate-500">Pick up right where you left off in the AI Studio</p>
              </div>
            </div>
            <Link
              to="/admin/studio"
              className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
            >
              Open Studio <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3 mt-4">
            {drafts.map((d) => {
              const schema = d.currentSchema || {}
              const pageCount = schema.pages?.length || 0
              const updatedStr = d.updatedAt
                ? new Date(d.updatedAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'Recently'

              return (
                <Link
                  key={d.draftId}
                  to={`/admin/studio/${d.draftId}`}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 shadow-2xs hover:border-indigo-300 hover:shadow-md transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-[10px] font-bold uppercase tracking-wider">
                        {schema.track || 'Draft'}
                      </span>
                      <span className="text-[11px] text-slate-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {updatedStr}
                      </span>
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 mt-2 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                      {schema.title || 'Untitled Module Draft'}
                    </h3>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1 font-medium text-slate-600">
                      <Layers className="h-3.5 w-3.5 text-slate-400" />
                      {pageCount} page{pageCount !== 1 ? 's' : ''}
                    </span>
                    <span className="inline-flex items-center gap-1 font-semibold text-indigo-600 group-hover:translate-x-0.5 transition-transform">
                      Resume <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* For Managers Section */}
      <div className="mt-12">
        <div className="flex items-center gap-2 text-slate-900 mb-4">
          <Users className="h-6 w-6 text-purple-600" />
          <h2 className="text-2xl font-bold">What Managers & Leads Can Do</h2>
        </div>
        <p className="text-sm text-slate-600 mb-6">
          Managers have full direct authority in Finder University to configure training for their departments and direct reports.
        </p>

        <div className="grid gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs flex flex-col">
            <div className="h-10 w-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-lg mb-4">
              <Users className="h-5 w-5" />
            </div>
            <h3 className="text-base font-bold text-slate-900">Enrol & Manage Team Roster</h3>
            <p className="mt-2 text-xs text-slate-600 leading-relaxed flex-1">
              Add new team members by email before they sign in. Automatically assign them to your direct reporting chain and assign department curricula.
            </p>
            <Link
              to="/admin/people"
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-purple-600 hover:text-purple-800"
            >
              Manage Team <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs flex flex-col">
            <div className="h-10 w-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-lg mb-4">
              <GraduationCap className="h-5 w-5" />
            </div>
            <h3 className="text-base font-bold text-slate-900">Create Custom Degrees</h3>
            <p className="mt-2 text-xs text-slate-600 leading-relaxed flex-1">
              Bundle existing courses (e.g. Sheets Basics + Claude Code + Finance AI) into custom degrees matching specific roles in your organization.
            </p>
            <Link
              to="/admin/degrees"
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
            >
              Create Degree <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs flex flex-col">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-lg mb-4">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <h3 className="text-base font-bold text-slate-900">Track Progress & Scores</h3>
            <p className="mt-2 text-xs text-slate-600 leading-relaxed flex-1">
              Monitor completion percentages, quiz scores, question breakdown, and inspect submitted Google Sheet URLs in real time.
            </p>
            <Link
              to="/admin"
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-800"
            >
              View Trainee Progress <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Building Modules with Claude */}
      <div className="mt-14">
        <div className="flex items-center gap-2 text-slate-900 mb-2">
          <Code2 className="h-6 w-6 text-indigo-600" />
          <h2 className="text-2xl font-bold">Building Modules with Claude</h2>
        </div>
        <p className="text-sm text-slate-600 mb-6">
          You do not need to write React from scratch. Use Claude Code in your terminal or Claude Desktop to generate modules, quiz questions, and spreadsheet validation logic.
        </p>

        {/* 4 Step Workflow */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 mb-1">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[11px]">1</span>
              CLONE & OPEN
            </div>
            <p className="text-xs text-slate-600">
              Clone the repository and start Claude Code in the project root.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 mb-1">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[11px]">2</span>
              PROMPT CLAUDE
            </div>
            <p className="text-xs text-slate-600">
              Copy a prompt template below and tell Claude what lesson you want to build.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 mb-1">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[11px]">3</span>
              TEST LOCALLY
            </div>
            <p className="text-xs text-slate-600">
              Run <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">npm run dev</code> to preview in browser, and <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">npm test</code>.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 mb-1">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[11px]">4</span>
              PR & SELF-MERGE
            </div>
            <p className="text-xs text-slate-600">
              Open a PR on GitHub. Once automated CI tests pass, merge to deploy live!
            </p>
          </div>
        </div>

        {/* Interactive Prompt Generator & Templates */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-4 mb-6">
            <div>
              <h3 className="text-base font-bold text-slate-900">Ready-to-Use Claude Prompts</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Copy these prompts directly into your Claude session to kick off module creation.
              </p>
            </div>

            <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl">
              <button
                onClick={() => setActiveTab('lesson')}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeTab === 'lesson'
                    ? 'bg-white text-slate-900 shadow-xs font-semibold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Paged Lesson
              </button>
              <button
                onClick={() => setActiveTab('sheet')}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeTab === 'sheet'
                    ? 'bg-white text-slate-900 shadow-xs font-semibold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Sheet Exercise
              </button>
              <button
                onClick={() => setActiveTab('degree')}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeTab === 'degree'
                    ? 'bg-white text-slate-900 shadow-xs font-semibold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Custom Degree
              </button>
            </div>
          </div>

          {activeTab === 'lesson' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">
                  Prompt: Create an Interactive Paged Lesson (Concepts + Worked Example + Quiz)
                </span>
              </div>
              <CopyBlock text={PROMPT_PAGED_LESSON} label="Copy Prompt" />
            </div>
          )}

          {activeTab === 'sheet' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">
                  Prompt: Create a Google Sheet Hands-On Exercise with Auto-Checking
                </span>
              </div>
              <CopyBlock text={PROMPT_SHEET_SUBMISSION} label="Copy Prompt" />
            </div>
          )}

          {activeTab === 'degree' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">
                  Prompt: Define a New Degree in Code
                </span>
              </div>
              <CopyBlock text={PROMPT_CUSTOM_DEGREE} label="Copy Prompt" />
            </div>
          )}
        </div>
      </div>

      {/* Architecture & Reusable Components */}
      <div className="mt-14 rounded-2xl border border-slate-200 bg-slate-100/70 p-6">
        <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2">
          <Layers className="h-5 w-5 text-indigo-600" />
          Reusable UI Component Toolkit
        </h3>
        <p className="text-xs text-slate-600 mb-4">
          All existing modules use shared components from <code className="bg-slate-200/80 px-1 py-0.5 rounded text-[11px]">src/components/</code>:
        </p>

        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white p-3.5">
            <span className="font-mono text-xs font-bold text-indigo-600">PagedLesson.jsx</span>
            <p className="text-xs text-slate-500 mt-1">Multi-page interactive lesson flow with next/prev pagination, callout boxes, and completion tracker.</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3.5">
            <span className="font-mono text-xs font-bold text-indigo-600">Quiz.jsx</span>
            <p className="text-xs text-slate-500 mt-1">Multiple-choice quizzes with randomized questions, instant explanations, and scoring.</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3.5">
            <span className="font-mono text-xs font-bold text-indigo-600">FormulaBreakdown.jsx</span>
            <p className="text-xs text-slate-500 mt-1">Interactive formula explainer highlighting arguments and parameters on hover.</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3.5">
            <span className="font-mono text-xs font-bold text-indigo-600">SheetSubmission.jsx</span>
            <p className="text-xs text-slate-500 mt-1">Live Google Sheet URL validator that reads and grades student sheets.</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3.5">
            <span className="font-mono text-xs font-bold text-indigo-600">DataFlowDiagram.jsx</span>
            <p className="text-xs text-slate-500 mt-1">Visual data piping flowcharts showing table transformation steps.</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3.5">
            <span className="font-mono text-xs font-bold text-indigo-600">CopyBlock.jsx</span>
            <p className="text-xs text-slate-500 mt-1">Dark code and formula blocks with one-click clipboard copying.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
